/**
 * How the app shows a person (app#401). ONE rule, used by the `UserLabel` component and by every
 * place that needs the label as plain text (a confirm message, a graph label).
 *
 *  - A person with a name: the name. The email is on hover, and a screen reader reads it too.
 *  - A person with no name: the email, with no hover.
 *  - An outsider (`member: false`, a service visitor who is not a member of the node's org): the
 *    email, marked as not a member.
 *  - A pattern (`*@acme.com` on a service's visitor list): as typed.
 *
 * ⚠ `member` is a fact from the router (router#1841 adds it to `service_share` visitors). Absent
 * means "not known", never "outsider": the app does not guess who is outside the org.
 *
 * ⚠ PURE, and imports nothing that needs Vite: `npm test` runs `userLabel.test.ts` with
 * `node --experimental-strip-types`.
 */

/** A person as the app knows them. Every field is optional: each source gives a different part. */
export type Person = {
  /** `profiles.full_name`, or the router's `name` (router#1841). `''` and null mean "no name". */
  name?: string | null
  email?: string | null
  /** The profile id. Shown only when there is nothing else. */
  id?: string | null
  /** From the router: true for a member of the node's org, false for an outsider. */
  member?: boolean | null
}

export type UserLabelParts = {
  /** What to show. Empty when the person has no name, no email and no id. */
  text: string
  /** The email, to show on hover. Only when `text` is the name. */
  hover?: string
  /** A person the router says is not a member of the org. */
  outsider: boolean
  /** A visitor pattern, such as `*@acme.com`. */
  pattern: boolean
}

/**
 * THE one place that builds a person's name. When the profile splits the name into first and last,
 * change this function only.
 *
 * ⚠ `||`-style: a missing name arrives as `''` as often as null (see CLAUDE.md), and a name of
 * spaces is no name.
 */
export const personName = (person: Pick<Person, 'name'>): string => (person.name ?? '').trim()

/** A visitor entry that names a group, not one address: `*@acme.com` or `@acme.com`. */
export const isEmailPattern = (entry: string): boolean => entry.includes('*') || entry.startsWith('@')

export function userLabel(person: Person): UserLabelParts {
  const email = (person.email ?? '').trim()
  const id = (person.id ?? '').trim()
  const name = personName(person)

  if (email && isEmailPattern(email)) return { text: email, outsider: false, pattern: true }
  if (person.member === false) return { text: email || name || id, outsider: true, pattern: false }
  if (name) return { text: name, ...(email && email !== name ? { hover: email } : {}), outsider: false, pattern: false }
  return { text: email || id, outsider: false, pattern: false }
}

/**
 * The props of the element the email tooltip wraps. The email is in that element as visually hidden
 * text, so a screen reader reads it with the name. MUI's `describeChild` tooltip ALSO puts the email
 * on the element: `title` while the tooltip is closed and `aria-describedby` while it is open (a
 * keyboard focus opens it). A screen reader then reads the email twice. These props remove both.
 * The label takes focus only when it has a tooltip, so the tooltip also opens from the keyboard.
 */
export const userLabelTargetProps = (label: Pick<UserLabelParts, 'hover'>) =>
  ({ tabIndex: label.hover ? 0 : undefined, title: undefined, 'aria-describedby': undefined }) as const

/** The label as plain text, for a place that cannot show a hover. `fallback` when there is nothing. */
export const userText = (person: Person, fallback = ''): string => userLabel(person).text || fallback

/**
 * The label with the email in the text: "Ada Lovelace (ada@example.com)". For a confirm dialog about
 * an act that is hard to undo (remove a member, transfer an org): two members can have the same
 * name, and a dialog has no hover.
 */
export function userTextWithEmail(person: Person, fallback = ''): string {
  const label = userLabel(person)
  if (!label.text) return fallback
  return label.hover ? `${label.text} (${label.hover})` : label.text
}

type RosterEntry = { userId: string; email: string; name?: string | null }

/**
 * Adds the name from the org's member list when the person has none. It matches by id, then by
 * email (case does not matter). It never sets `member`: the roster is the ACTIVE org's, which is not
 * always the node's org, so a miss does not prove an outsider. An empty roster (a Member cannot read
 * it, D-16) changes nothing.
 */
export function withRosterName(person: Person, roster: readonly RosterEntry[]): Person {
  if (personName(person)) return person
  const email = (person.email ?? '').trim().toLowerCase()
  const hit =
    (person.id ? roster.find(m => m.userId === person.id) : undefined) ??
    (email ? roster.find(m => m.email.toLowerCase() === email) : undefined)
  const name = hit ? personName(hit) : ''
  return name ? { ...person, name, email: person.email || hit?.email } : person
}
